/* <gr-accordion> — button + panel disclosure.
   A native <details>/<summary> cannot reproduce the design's exact row layout
   and plus/minus mark without fighting the default marker across browsers, so
   this implements the same semantics explicitly: the trigger is a real button
   carrying aria-expanded and aria-controls, the panel is a plain region.
   Without JS every panel renders open, so the content is never unreachable. */

class GrAccordion extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    this.querySelectorAll('[data-acc-btn]').forEach((btn) => {
      const panel = this.panelFor(btn);
      if (!panel) return;
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      panel.hidden = !expanded;
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this._bound = false;
  }

  panelFor(btn) {
    const id = btn.getAttribute('aria-controls');
    return id ? this.querySelector('#' + CSS.escape(id)) : null;
  }

  onClick(event) {
    const btn = event.target.closest('[data-acc-btn]');
    if (!btn || !this.contains(btn)) return;

    const panel = this.panelFor(btn);
    if (!panel) return;

    const willExpand = btn.getAttribute('aria-expanded') !== 'true';

    if (willExpand && this.hasAttribute('single')) {
      this.querySelectorAll('[data-acc-btn][aria-expanded="true"]').forEach((other) => {
        if (other === btn) return;
        other.setAttribute('aria-expanded', 'false');
        const otherPanel = this.panelFor(other);
        if (otherPanel) otherPanel.hidden = true;
      });
    }

    btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
    panel.hidden = !willExpand;
  }
}

if (!customElements.get('gr-accordion')) customElements.define('gr-accordion', GrAccordion);
